import React, { useEffect, useState } from 'react';
import { Link, router, useForm } from '@inertiajs/react';
import clsx from 'clsx';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import Avatar from '@/components/common/Avatar';
import Breadcrumbs from '@/components/Breadcrumbs';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import useRoutes from '@/hooks/useRoutes';
import { User } from '@/types';
import {
    Edit,
    Eye,
    BookOpen,
    Mail,
    LogIn,
    ClipboardList,
    Star,
} from 'lucide-react';

interface CompletedClass {
    id: number;
    class_title: string;
    completed_at: string;
}

interface EvaluationAttempt {
    id: number;
    evaluation_title: string;
    score: number;
    total_score: number;
    percentage: number;
    passed: boolean;
    completed_at: string | null;
}

interface AssignmentSubmission {
    id: number;
    assignment_title: string;
    status: string;
    teacher_comment: string;
    reviewed_at: string | null;
    reviewer_name: string | null;
}

interface CredentialsEmail {
    id: number;
    subject: string;
    view_url: string;
    created_at: string;
}

interface EmailData {
    id: number;
    subject: string;
    view_url: string;
    created_at: string;
}

interface LoginHistoryRecord {
    id: number;
    timestamp: string;
    ip_address: string;
    user_agent: string;
}

interface ClassViewRecord {
    id: number;
    class_title: string;
    viewed_at: string;
}

interface FichaCourseEntry {
    enrollment_id: number;
    starts_at: string | null;
    expires_at: string | null;
    course: { id: number; title: string };
    progress_percentage: number;
    total_classes: number;
    completed_classes_count: number;
    evaluation_attempts: EvaluationAttempt[];
    certificates: any[];
    completed_classes: CompletedClass[];
    assignments: AssignmentSubmission[];
}

interface FichaCategory {
    category: string;
    courses: FichaCourseEntry[];
}

interface SpecialCourse {
    id: number;
    title: string;
}

interface DetailProps {
    student: User;
    credentials_email: CredentialsEmail | null;
    all_emails: EmailData[];
    login_history: LoginHistoryRecord[];
    class_views: ClassViewRecord[];
    ficha: FichaCategory[];
    special_courses: SpecialCourse[];
    can_edit_enrollment_dates: boolean;
    can_pause_student: boolean;
    is_paused: boolean;
}

interface AssignSpecialCourseTabProps {
    student: User;
    specialCourses: SpecialCourse[];
    assignUrl: string;
}

const AssignSpecialCourseTab: React.FC<AssignSpecialCourseTabProps> = ({ specialCourses, assignUrl }) => {
    const { data, setData, post, processing } = useForm({ course_id: '' });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        post(assignUrl);
    };

    return (
        <div className="max-w-md">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Asignar curso especial</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                        Curso
                    </label>
                    <select
                        value={data.course_id}
                        onChange={(e) => setData('course_id', e.target.value)}
                        className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-theme-primary focus:outline-none focus:ring-1 focus:ring-theme-primary"
                        required
                    >
                        <option value="">Selecciona un curso...</option>
                        {specialCourses.map((course) => (
                            <option key={course.id} value={course.id}>
                                {course.title}
                            </option>
                        ))}
                    </select>
                </div>
                <PrimaryButton type="submit" disabled={processing || !data.course_id}>
                    <Star size={16} className="mr-2" />
                    Asignar curso
                </PrimaryButton>
            </form>
        </div>
    );
};

interface EnrollmentDatesRowProps {
    enrollmentId: number;
    startsAt: string | null;
    expiresAt: string | null;
    canEdit: boolean;
    updateUrl: string;
    formatDate: (d: string | null) => string;
}

const EnrollmentDatesRow: React.FC<EnrollmentDatesRowProps> = ({
    enrollmentId,
    startsAt,
    expiresAt,
    canEdit,
    updateUrl,
    formatDate,
}) => {
    const [editing, setEditing] = useState(false);
    const { data, setData, post, processing } = useForm({
        expires_at: expiresAt ? expiresAt.slice(0, 10) : '',
    });

    const handleSave = (e: React.FormEvent) => {
        e.preventDefault();
        post(updateUrl, { onSuccess: () => setEditing(false) });
    };

    return (
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-500">
            <span>
                <span className="font-medium text-gray-700">Inicio:</span>{' '}
                {formatDate(startsAt)}
            </span>
            <span className="flex items-center gap-2">
                <span className="font-medium text-gray-700">Vencimiento:</span>{' '}
                {editing ? (
                    <form onSubmit={handleSave} className="flex items-center gap-2">
                        <input
                            type="date"
                            value={data.expires_at}
                            onChange={(e) => setData('expires_at', e.target.value)}
                            className="rounded border border-gray-300 px-2 py-0.5 text-sm focus:border-theme-primary focus:outline-none"
                        />
                        <button
                            type="submit"
                            disabled={processing}
                            className="rounded bg-theme-primary px-2 py-0.5 text-xs font-medium text-theme-on-primary hover:opacity-90 disabled:opacity-50"
                        >
                            Guardar
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditing(false)}
                            className="rounded bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-300"
                        >
                            Cancelar
                        </button>
                    </form>
                ) : (
                    <>
                        {expiresAt ? formatDate(expiresAt) : 'Sin vencimiento'}
                        {canEdit && (
                            <button
                                type="button"
                                onClick={() => setEditing(true)}
                                className="ml-1 text-xs text-blue-600 underline hover:text-blue-800"
                            >
                                Editar
                            </button>
                        )}
                    </>
                )}
            </span>
        </div>
    );
};

const COURSE_SUB_TABS = [
    { id: 'evaluations', label: 'Evaluaciones' },
    { id: 'certificates', label: 'Certificados' },
    { id: 'classes', label: 'Clases Completadas' },
    { id: 'assignments', label: 'Tareas' },
] as const;

interface PauseButtonProps {
    studentId: number;
    isPaused: boolean;
    canPause: boolean;
    pauseUrl: string;
}

const PauseButton: React.FC<PauseButtonProps> = ({ studentId, isPaused, canPause, pauseUrl }) => {
    const { post, processing, setData } = useForm({ action: isPaused ? 'unpause' : 'pause' });

    useEffect(() => {
        setData('action', isPaused ? 'unpause' : 'pause');
    }, [isPaused]);

    if (!canPause) {
        return null;
    }

    const handleClick = () => {
        const confirmMsg = isPaused
            ? '¿Reactivar la cuenta de este estudiante?'
            : '¿Pausar la cuenta de este estudiante? No podrá acceder a los cursos.';
        if (!window.confirm(confirmMsg)) {
            return;
        }
        post(pauseUrl);
    };

    return (
        <button
            onClick={handleClick}
            disabled={processing}
            className={clsx(
                'inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                isPaused
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-red-100 text-red-800 hover:bg-red-200',
            )}
        >
            {isPaused ? 'Reactivar cuenta' : 'Pausar cuenta'}
        </button>
    );
};

const DetailContent: React.FC<DetailProps> = ({
    student,
    credentials_email,
    all_emails,
    login_history,
    class_views,
    ficha,
    special_courses,
    can_edit_enrollment_dates,
    can_pause_student,
    is_paused,
}) => {
    const { route } = useRoutes();

    const validTabs = special_courses.length > 0
        ? ['ficha', 'emails', 'login_history', 'class_views', 'assign_special_course']
        : ['ficha', 'emails', 'login_history', 'class_views'];

    const getInitialTab = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const tab = urlParams.get('tab');
        return tab && validTabs.includes(tab) ? tab : 'ficha';
    };

    const getInitialCourseSubTab = (): Record<number, string> => {
        const urlParams = new URLSearchParams(window.location.search);
        const courseId = urlParams.get('course');
        const subtab = urlParams.get('subtab');
        if (courseId && subtab) {
            return { [parseInt(courseId)]: subtab };
        }
        return {};
    };

    const [activeTab, setActiveTab] = useState<string>(getInitialTab);
    const [courseSubTab, setCourseSubTab] = useState<Record<number, string>>(getInitialCourseSubTab);

    const getCourseSubTab = (courseId: number) => courseSubTab[courseId] ?? 'evaluations';

    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId);
        const url = new URL(window.location.href);
        url.searchParams.delete('course');
        url.searchParams.delete('subtab');
        if (tabId === 'ficha') {
            url.searchParams.delete('tab');
        } else {
            url.searchParams.set('tab', tabId);
        }
        router.visit(url.pathname + url.search, {
            preserveState: true,
            preserveScroll: true,
        });
    };

    const handleCourseSubTabChange = (courseId: number, subtab: string) => {
        setCourseSubTab((prev) => ({ ...prev, [courseId]: subtab }));
        const url = new URL(window.location.href);
        url.searchParams.delete('tab');
        url.searchParams.set('course', String(courseId));
        url.searchParams.set('subtab', subtab);
        router.visit(url.pathname + url.search, {
            preserveState: true,
            preserveScroll: true,
        });
    };

    useEffect(() => {
        const handlePopState = () => {
            setActiveTab(getInitialTab());
            setCourseSubTab(getInitialCourseSubTab());
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const tabs = [
        { id: 'ficha', label: 'Ficha del alumno', icon: ClipboardList },
        { id: 'emails', label: 'Emails', icon: Mail },
        { id: 'login_history', label: 'Historial de Inicio de Sesión', icon: LogIn },
        { id: 'class_views', label: 'Historial de Clases Vistas', icon: BookOpen },
        ...(special_courses.length > 0
            ? [{ id: 'assign_special_course', label: 'Asignar curso especial', icon: Star }]
            : []),
    ];

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatDateTime = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <StaffLayout>
            <div className="mx-auto max-w-7xl">
                {/* Breadcrumbs */}
                <Breadcrumbs
                    items={[
                        {
                            label: 'Estudiantes',
                            href: route('staff-students').toString(),
                        },
                        {
                            label: student.full_name,
                        },
                    ]}
                    className="mb-6"
                />

                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                            <Avatar user={student} size="xl" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">
                                    {student.full_name}
                                </h1>
                                <p className="mt-1 text-gray-600">
                                    {student.email}
                                </p>
                                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                                    <span>
                                        Academia: {student.current_academy?.name || '-'}
                                    </span>
                                    <span>Puntos: {student.points || 0}</span>
                                    <span>
                                        Rango: {student.rank?.name || '-'}
                                    </span>
                                    {student.is_active ? (
                                        <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
                                            Activo
                                        </span>
                                    ) : (
                                        <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">
                                            Inactivo
                                        </span>
                                    )}
                                    {is_paused && (
                                        <span className="inline-flex rounded-full bg-orange-100 px-2 text-xs font-semibold leading-5 text-orange-800">
                                            Pausada
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex space-x-3">
                            <PauseButton
                                studentId={student.id}
                                isPaused={is_paused}
                                canPause={can_pause_student}
                                pauseUrl={route('staff-students-pause', { student_id: student.id }).toString()}
                            />
                            <Link
                                href={route('staff-students-edit', {
                                    student_id: student.id,
                                }).toString()}
                            >
                                <PrimaryButton>
                                    <Edit size={16} className="mr-2" />
                                    Editar
                                </PrimaryButton>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                className={clsx(
                                    'flex items-center whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                                    activeTab === tab.id
                                        ? 'bg-theme-primary text-theme-on-primary'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                                )}
                            >
                                <Icon size={16} className="mr-2" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Tab Content */}
                <div className="rounded-lg bg-white p-6 shadow">
                    {activeTab === 'emails' && (
                        <div>
                            {all_emails.length === 0 ? (
                                <p className="text-center text-gray-500">
                                    No se han enviado emails a este estudiante
                                </p>
                            ) : (
                                <div className="overflow-hidden rounded-lg bg-white shadow">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Asunto
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Fecha
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Acciones
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {all_emails.map((email) => (
                                                <tr
                                                    key={email.id}
                                                    className={
                                                        credentials_email?.id === email.id
                                                            ? 'bg-blue-50'
                                                            : ''
                                                    }
                                                >
                                                    <td className="whitespace-nowrap px-6 py-4">
                                                        <div className="flex items-center">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {email.subject}
                                                                {credentials_email?.id === email.id && (
                                                                    <span className="ml-2 inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold leading-5 text-blue-800">
                                                                        Credenciales
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4">
                                                        <div className="text-sm text-gray-500">
                                                            {formatDateTime(email.created_at)}
                                                        </div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                                        <a
                                                            href={email.view_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center text-blue-600 hover:text-blue-900"
                                                        >
                                                            <Mail size={16} className="mr-1" />
                                                            Ver Email
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'login_history' && (
                        <div>
                            {login_history.length === 0 ? (
                                <p className="text-center text-gray-500">
                                    No hay registros de inicio de sesión
                                </p>
                            ) : (
                                <div className="overflow-hidden rounded-lg bg-white shadow">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Fecha y Hora
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Dirección IP
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Navegador/Dispositivo
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {login_history.map((login) => (
                                                <tr key={login.id}>
                                                    <td className="whitespace-nowrap px-6 py-4">
                                                        <div className="text-sm text-gray-900">
                                                            {formatDateTime(login.timestamp)}
                                                        </div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4">
                                                        <div className="text-sm text-gray-500">
                                                            {login.ip_address}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm text-gray-500">
                                                            {login.user_agent.length > 100
                                                                ? `${login.user_agent.substring(0, 100)}...`
                                                                : login.user_agent}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'class_views' && (
                        <div>
                            {class_views.length === 0 ? (
                                <p className="text-center text-gray-500">
                                    No hay historial de clases vistas
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Clase
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                                    Fecha de visualización
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {class_views.map((classView) => (
                                                <tr key={classView.id}>
                                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                                        {classView.class_title}
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                        {formatDateTime(classView.viewed_at)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'assign_special_course' && (
                        <AssignSpecialCourseTab
                            student={student}
                            specialCourses={special_courses}
                            assignUrl={route('staff-students-assign-special-course', { student_id: student.id }).toString()}
                        />
                    )}

                    {activeTab === 'ficha' && (
                        <div className="space-y-8">
                            {ficha.length === 0 ? (
                                <p className="text-center text-gray-500">
                                    No hay inscripciones
                                </p>
                            ) : (
                                ficha.map((group) => (
                                    <div key={group.category}>
                                        <h2 className="mb-4 text-lg font-semibold text-gray-900">
                                            {group.category}
                                        </h2>
                                        <div className="space-y-6">
                                            {group.courses.map((courseEntry) => {
                                                const { enrollment_id, starts_at, expires_at, course, progress_percentage, total_classes, completed_classes_count, evaluation_attempts, certificates: courseCerts, completed_classes: courseClasses } = courseEntry;
                                                const subTab = getCourseSubTab(course.id);
                                                return (
                                                    <div
                                                        key={course.id}
                                                        className="overflow-hidden rounded-lg border border-gray-200"
                                                    >
                                                        {/* Card header with progress */}
                                                        <div className="p-4">
                                                            <h3 className="mb-3 font-semibold text-gray-900">
                                                                {course.title}
                                                            </h3>
                                                            <div className="mb-1 flex justify-between text-sm">
                                                                <span className="text-gray-600">Progreso</span>
                                                                <span className="font-medium text-gray-900">
                                                                    {completed_classes_count} / {total_classes} clases ({progress_percentage}%)
                                                                </span>
                                                            </div>
                                                            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                                                                <div
                                                                    className="h-full bg-theme-primary"
                                                                    style={{ width: `${progress_percentage}%` }}
                                                                />
                                                            </div>
                                                            <EnrollmentDatesRow
                                                                enrollmentId={enrollment_id}
                                                                startsAt={starts_at}
                                                                expiresAt={expires_at}
                                                                canEdit={can_edit_enrollment_dates}
                                                                updateUrl={route('staff-students-update-enrollment-expires-at', { student_id: student.id, enrollment_id }).toString()}
                                                                formatDate={formatDate}
                                                            />
                                                        </div>

                                                        {/* Sub-tabs */}
                                                        <div className="flex gap-2 border-t border-gray-100 bg-gray-50 px-4 py-3">
                                                            {COURSE_SUB_TABS.map((st) => (
                                                                <button
                                                                    key={st.id}
                                                                    type="button"
                                                                    onClick={() => handleCourseSubTabChange(course.id, st.id)}
                                                                    className={clsx(
                                                                        'whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                                                                        subTab === st.id
                                                                            ? 'bg-theme-primary text-theme-on-primary'
                                                                            : 'bg-white text-gray-600 hover:bg-gray-200',
                                                                    )}
                                                                >
                                                                    {st.label}
                                                                </button>
                                                            ))}
                                                        </div>

                                                        {/* Sub-tab content */}
                                                        <div className="p-4">
                                                            {subTab === 'evaluations' && (
                                                                evaluation_attempts.length === 0 ? (
                                                                    <p className="text-sm text-gray-500">Sin intentos de evaluación</p>
                                                                ) : (
                                                                    <div className="overflow-x-auto">
                                                                        <table className="min-w-full divide-y divide-gray-200">
                                                                            <thead className="bg-gray-50">
                                                                                <tr>
                                                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Evaluación</th>
                                                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Puntaje</th>
                                                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Estado</th>
                                                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Fecha</th>
                                                                                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Acciones</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="divide-y divide-gray-200 bg-white">
                                                                                {evaluation_attempts.map((attempt) => (
                                                                                    <tr key={attempt.id}>
                                                                                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{attempt.evaluation_title}</td>
                                                                                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                                                                                            {attempt.score}/{attempt.total_score} ({attempt.percentage.toFixed(1)}%)
                                                                                        </td>
                                                                                        <td className="whitespace-nowrap px-4 py-3">
                                                                                            {attempt.passed ? (
                                                                                                <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">Aprobado</span>
                                                                                            ) : (
                                                                                                <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">Reprobado</span>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{formatDateTime(attempt.completed_at)}</td>
                                                                                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                                                                                            <Link
                                                                                                href={route('staff-students-evaluation-attempt-detail', {
                                                                                                    student_id: student.id,
                                                                                                    attempt_id: attempt.id,
                                                                                                }).toString()}
                                                                                                className="inline-flex items-center text-blue-600 hover:text-blue-900"
                                                                                            >
                                                                                                <Eye size={16} className="mr-1" />
                                                                                                Ver detalle
                                                                                            </Link>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                )
                                                            )}

                                                            {subTab === 'certificates' && (
                                                                courseCerts.length === 0 ? (
                                                                    <p className="text-sm text-gray-500">Sin certificados</p>
                                                                ) : (
                                                                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                                                        {courseCerts.map((cert) => (
                                                                            <div key={cert.id} className="rounded-lg border border-gray-200 p-4">
                                                                                {cert.image_url && (
                                                                                    <img src={cert.image_url} alt={cert.evaluation?.title} className="mb-3 w-full rounded" />
                                                                                )}
                                                                                <h4 className="font-semibold text-gray-900">{cert.evaluation?.title}</h4>
                                                                                <p className="mt-2 text-xs text-gray-400">
                                                                                    Emitido: {cert.issued_at ? formatDate(cert.issued_at) : 'N/A'}
                                                                                </p>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )
                                                            )}

                                                            {subTab === 'classes' && (
                                                                courseClasses.length === 0 ? (
                                                                    <p className="text-sm text-gray-500">Sin clases completadas</p>
                                                                ) : (
                                                                    <div className="overflow-x-auto">
                                                                        <table className="min-w-full divide-y divide-gray-200">
                                                                            <thead className="bg-gray-50">
                                                                                <tr>
                                                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Clase</th>
                                                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Fecha</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="divide-y divide-gray-200 bg-white">
                                                                                {courseClasses.map((cc, idx) => (
                                                                                    <tr key={idx}>
                                                                                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{cc.class_title}</td>
                                                                                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{formatDateTime(cc.completed_at)}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                )
                                                            )}

                                                            {subTab === 'assignments' && (
                                                                courseEntry.assignments.length === 0 ? (
                                                                    <p className="text-sm text-gray-500">Sin tareas entregadas</p>
                                                                ) : (
                                                                    <div className="overflow-x-auto">
                                                                        <table className="min-w-full divide-y divide-gray-200">
                                                                            <thead className="bg-gray-50">
                                                                                <tr>
                                                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Tarea</th>
                                                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Estado</th>
                                                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Comentario</th>
                                                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Revisado</th>
                                                                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Revisor</th>
                                                                                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Acciones</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="divide-y divide-gray-200 bg-white">
                                                                                {courseEntry.assignments.map((submission) => (
                                                                                    <tr key={submission.id}>
                                                                                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{submission.assignment_title}</td>
                                                                                        <td className="whitespace-nowrap px-4 py-3">
                                                                                            <span className={clsx(
                                                                                                'inline-flex rounded-full px-2 text-xs font-semibold leading-5',
                                                                                                submission.status === 'approved' ? 'bg-green-100 text-green-800'
                                                                                                : submission.status === 'rejected' ? 'bg-red-100 text-red-800'
                                                                                                : 'bg-yellow-100 text-yellow-800',
                                                                                            )}>
                                                                                                {submission.status === 'approved' ? 'Aprobado'
                                                                                                : submission.status === 'rejected' ? 'Rechazado'
                                                                                                : 'Pendiente'}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="max-w-xs truncate px-4 py-3 text-sm text-gray-600">{submission.teacher_comment || '—'}</td>
                                                                                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{formatDateTime(submission.reviewed_at)}</td>
                                                                                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{submission.reviewer_name || '—'}</td>
                                                                                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                                                                                            <div className="flex items-center justify-end gap-2">
                                                                                                <Link
                                                                                                    href={route('staff-assignment-submissions-detail', { submission_id: submission.id }).toString()}
                                                                                                    className="inline-flex items-center text-blue-600 hover:text-blue-900"
                                                                                                >
                                                                                                    <Eye size={16} />
                                                                                                </Link>
                                                                                                <Link
                                                                                                    href={route('staff-assignment-submissions-edit', { submission_id: submission.id }).toString()}
                                                                                                    className="inline-flex items-center text-blue-600 hover:text-blue-900"
                                                                                                >
                                                                                                    <Edit size={16} />
                                                                                                </Link>
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </StaffLayout>
    );
};

const Detail: React.FC<DetailProps> = (props) => {
    return (
        <ThemeWrapper>
            <DetailContent {...props} />
        </ThemeWrapper>
    );
};

export default Detail;
